import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, Lock, Star, Mail, ArrowLeft, Shield, Users, Zap, Sparkles, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';

const Premium = ({ isModal = false }) => {
    const navigate = useNavigate();
    const [selectedPlan, setSelectedPlan] = useState('yearly');
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const handlePurchase = () => {
        alert('Payment integration coming soon! You selected: ' + (selectedPlan === 'yearly' ? 'Yearly Plan' : 'Monthly Plan'));
    };

    const features = [
        { name: 'Families', free: '1', premium: 'Unlimited' },
        { name: 'Family Members', free: '2', premium: 'Unlimited' },
        { name: 'To-do and Shopping Lists', free: 'Unlimited', premium: 'Unlimited' },
        { name: 'Secrets Vault', free: '25', premium: 'Unlimited' },
        { name: 'Documents', free: '25', premium: 'Unlimited' },
        { name: 'Appliances & Devices', free: '25', premium: 'Unlimited' },
        { name: 'Vehicles', free: '5', premium: 'Unlimited' },
        { name: 'Family Calendar', free: 'One-way Sync', premium: 'Two-way Sync', comingSoon: true },
        { name: 'Offline Access', free: 'locked', premium: true },
        { name: 'AI Insights', free: 'locked', premium: 'Coming soon', hasIcon: true },
        { name: 'Themes', free: 'locked', premium: 'Coming soon' },
        { name: 'Recover Deleted Items', free: 'locked', premium: 'Coming soon' },
        { name: 'Priority Support', free: 'locked', premium: true },
    ];

    const faqs = [
        {
            question: 'Can I cancel anytime?',
            answer: 'Yes! You can cancel your subscription at any time from your account settings. Your premium features will remain active until the end of your billing period.'
        },
        {
            question: 'Will my family members also benefit from Premium?',
            answer: 'Absolutely! When you purchase Premium, all members of your family groups get access to premium features. One subscription benefits everyone.'
        },
        {
            question: 'Can I use Premium on multiple devices?',
            answer: 'Yes! Your Premium subscription works across all your devices - phone, tablet, and web. Simply log in with your account on any device.'
        }
    ];

    return (
        <div className={`relative ${isModal ? 'h-full flex flex-col min-h-full' : 'min-h-screen'}`}>
            {/* Pulse animation for buttons */}
            <style>{`
                @keyframes pulse-glow {
                    0%, 100% {
                        box-shadow: 0 0 25px rgba(59, 130, 246, 0.6), 0 0 50px rgba(236, 72, 153, 0.6), 0 20px 60px rgba(0, 0, 0, 0.3);
                        transform: scale(1);
                    }
                    50% {
                        box-shadow: 0 0 35px rgba(59, 130, 246, 0.9), 0 0 70px rgba(236, 72, 153, 0.9), 0 20px 70px rgba(0, 0, 0, 0.4);
                        transform: scale(1.02);
                    }
                }
                .btn-pulse {
                    animation: pulse-glow 2s ease-in-out infinite;
                }
            `}</style>

            {/* Glass background for modal */}
            {isModal && (
                <div
                    className="absolute inset-0 z-0"
                    style={{
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        borderRadius: '1rem'
                    }}
                />
            )}

            {/* Static Diagonal Gradient Background - Top-Left to Bottom-Right (only when not modal) */}
            {!isModal && (
                <div
                    className="fixed inset-0 z-0"
                    style={{
                        background: 'linear-gradient(135deg, rgb(59, 130, 246) 0%, rgb(236, 72, 153) 100%)'
                    }}
                />
            )}

            {/* Scrollable content wrapper for modal */}
            <div className={`relative z-10 ${isModal ? 'flex-1 overflow-y-auto' : ''}`}>

            {/* Navigation - Hidden when shown as modal */}
            {!isModal && (
                <nav className="fixed top-0 w-full z-50">
                    <div className="relative w-full h-20 py-4">
                        {/* Logo - Positioned 10% from left */}
                        <div
                            className="fixed flex items-center space-x-3"
                            style={{ left: '10%', top: '1.5rem' }}
                        >
                            <img src="/logo.png" alt="mifamli logo" className="h-12 w-12" />
                            <span className="text-3xl font-bold text-white drop-shadow-lg">mifamli</span>
                        </div>

                        {/* Button - Positioned 10% from right */}
                        <div
                            className="fixed flex items-center gap-3"
                            style={{ right: '10%', top: '1.5rem' }}
                        >
                            <button
                                onClick={() => navigate('/')}
                                className="btn-pulse relative px-8 py-4 rounded-xl font-bold text-xl text-white hover:scale-105 transform transition-all duration-200 backdrop-blur-md border-4 border-white/50 overflow-hidden group"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.8) 0%, rgba(236, 72, 153, 0.8) 100%)',
                                    boxShadow: '0 0 30px rgba(59, 130, 246, 0.7), 0 0 60px rgba(236, 72, 153, 0.7), 0 20px 70px rgba(0, 0, 0, 0.3)'
                                }}
                            >
                                <span className="relative z-10 flex items-center gap-3">
                                    <ArrowLeft className="w-5 h-5" />
                                    Back to Home
                                </span>
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/40 to-pink-500/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                            </button>
                        </div>
                    </div>
                </nav>
            )}

            {/* Hero Section with Image */}
            <section className={`relative z-10 ${isModal ? 'pt-8' : 'pt-24'} pb-16 px-4`}>
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                            Upgrade to Premium
                        </h1>
                        <p className="text-3xl sm:text-4xl text-white/90 max-w-2xl mx-auto">
                            Unlock powerful features to keep your family even more organized
                        </p>
                    </div>
                    
                    {/* Hero Image with Logo */}
                    <div className="max-w-4xl mx-auto relative">
                        <img
                            src="https://images.unsplash.com/photo-1511895426328-dc8714191300?w=1200&h=600&fit=crop"
                            alt="Happy family"
                            className="rounded-2xl shadow-2xl w-full object-cover"
                        />
                        <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
                            <img src="/logo.png" alt="mifamli logo" className="h-6 w-6" />
                            <span className="font-bold text-primary-blue">mifamli Premium</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="relative z-10 py-16 px-4">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white text-center mb-12">
                        Why Go Premium?
                    </h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-white/90 backdrop-blur-sm text-center p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300">
                            <div className="bg-primary-green/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Shield className="w-8 h-8 text-primary-green" />
                            </div>
                            <h3 className="text-3xl font-bold text-primary-blue mb-3">Unlimited Storage</h3>
                            <p className="text-2xl text-gray-600">
                                Store unlimited passwords, documents, and family information securely
                            </p>
                        </div>
                        <div className="bg-white/90 backdrop-blur-sm text-center p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300">
                            <div className="bg-primary-orange/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Users className="w-8 h-8 text-primary-orange" />
                            </div>
                            <h3 className="text-3xl font-bold text-primary-blue mb-3">Priority Support</h3>
                            <p className="text-2xl text-gray-600">
                                Get help faster with dedicated premium support team
                            </p>
                        </div>
                        <div className="bg-white/90 backdrop-blur-sm text-center p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300">
                            <div className="bg-blue-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Zap className="w-8 h-8 text-blue-500" />
                            </div>
                            <h3 className="text-3xl font-bold text-primary-blue mb-3">Advanced Features</h3>
                            <p className="text-2xl text-gray-600">
                                Access exclusive features like analytics, themes, and offline mode
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonial */}
            <section className="relative z-10 py-16 px-4">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-white/90 backdrop-blur-sm p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                        <div className="flex items-center mb-6">
                            <img
                                src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=100&h=100&fit=crop"
                                alt="Priya"
                                className="w-16 h-16 rounded-full object-cover mr-4"
                            />
                            <div>
                                <p className="font-bold text-primary-blue text-2xl">Priya Sharma</p>
                                <p className="text-xl text-gray-600">Premium User since 2024</p>
                                <div className="flex gap-1 mt-1">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <p className="text-gray-700 text-2xl italic leading-relaxed">
                            "Premium has transformed how we manage our household. The unlimited password vault alone is worth it - no more forgetting important credentials! The offline access means I can check our family calendar even without internet. Best investment for our family!"
                        </p>
                    </div>
                </div>
            </section>

            {/* App Store Reviews */}
            <section className="relative z-10 py-16 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">Loved by Thousands</h2>
                    <div className="flex items-center justify-center gap-4 mb-4">
                        <div className="flex gap-1">
                            {[...Array(5)].map((_, i) => (
                                <Star key={i} className="w-10 h-10 text-yellow-500 fill-yellow-500" />
                            ))}
                        </div>
                        <span className="text-5xl font-bold text-white">4.8</span>
                    </div>
                    <p className="text-2xl text-white/90">
                        Average rating from <span className="font-bold">2,500+ reviews</span> on App Store & Play Store
                    </p>
                </div>
            </section>

            {/* Feature Comparison */}
            <section className="relative z-10 py-16 px-4">
                <div className="max-w-5xl mx-auto">
                    <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white text-center mb-12">
                        Free vs Premium
                    </h2>
                    <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden">
                        <div className="grid grid-cols-3 bg-primary-blue text-white p-4 font-bold text-xl">
                            <div className="col-span-1">Feature</div>
                            <div className="text-center">Free</div>
                            <div className="text-center">Premium</div>
                        </div>
                        {features.map((feature, index) => (
                            <div key={index} className={`grid grid-cols-3 p-4 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                                <div className="col-span-1 text-gray-800 font-medium flex items-center gap-2 text-lg">
                                    {feature.name}
                                    {feature.hasIcon && (
                                        <Sparkles className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                                    )}
                                    {feature.comingSoon && (
                                        <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full font-normal">Coming soon</span>
                                    )}
                                </div>
                                <div className="flex justify-center items-center">
                                    {feature.free === 'locked' ? (
                                        <Lock className="w-6 h-6 text-gray-300" />
                                    ) : feature.free === true ? (
                                        <Check className="w-6 h-6 text-primary-green" />
                                    ) : (
                                        <span className="text-gray-700 font-semibold text-center">{feature.free}</span>
                                    )}
                                </div>
                                <div className="flex justify-center items-center">
                                    {feature.premium === true ? (
                                        <Check className="w-6 h-6 text-primary-green" />
                                    ) : (
                                        <span className={`font-bold text-center ${feature.premium === 'Coming soon' ? 'text-yellow-600' : 'text-primary-green'}`}>{feature.premium}</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Cards */}
            <section className="relative z-10 py-16 px-4">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white text-center mb-12">
                        Choose Your Plan
                    </h2>
                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        {/* Monthly Plan */}
                        <div 
                            onClick={() => setSelectedPlan('monthly')}
                            className={`bg-white/90 backdrop-blur-sm border-4 rounded-2xl p-8 cursor-pointer transition-all hover:shadow-xl flex flex-col ${
                                selectedPlan === 'monthly' ? 'border-primary-blue shadow-2xl' : 'border-white/50'
                            }`}
                        >
                            <div className="text-center flex-1 flex flex-col">
                                <h3 className="text-3xl font-bold text-primary-blue mb-2">Monthly</h3>
                                <div className="mb-6">
                                    <span className="text-5xl font-bold text-primary-blue">₹399</span>
                                    <span className="text-gray-600 text-xl">/month</span>
                                </div>
                                <p className="text-2xl text-gray-600 mb-4">Total: ₹4,788/year</p>
                                <div className="h-6 mb-4"></div>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedPlan('monthly');
                                    }}
                                    className={`w-full py-3 rounded-lg font-semibold transition-all mt-auto text-lg ${
                                        selectedPlan === 'monthly'
                                            ? 'bg-primary-blue text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {selectedPlan === 'monthly' ? 'Selected' : 'Select Plan'}
                                </button>
                            </div>
                        </div>

                        {/* Yearly Plan */}
                        <div 
                            onClick={() => setSelectedPlan('yearly')}
                            className={`bg-white/90 backdrop-blur-sm border-4 rounded-2xl p-8 cursor-pointer transition-all hover:shadow-xl relative flex flex-col ${
                                selectedPlan === 'yearly' ? 'border-primary-green shadow-2xl' : 'border-white/50'
                            }`}
                        >
                            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-primary-green text-white px-4 py-1 rounded-full text-sm font-bold">
                                BEST VALUE
                            </div>
                            <div className="text-center flex-1 flex flex-col">
                                <h3 className="text-3xl font-bold text-primary-blue mb-2">Yearly</h3>
                                <div className="mb-6">
                                    <span className="text-5xl font-bold text-primary-green">₹299</span>
                                    <span className="text-gray-600 text-xl">/month</span>
                                </div>
                                <p className="text-2xl text-gray-600 mb-2">Total: ₹3,588/year</p>
                                <p className="text-primary-green font-bold mb-4 text-xl">Save ₹1,200 (25% OFF)</p>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedPlan('yearly');
                                    }}
                                    className={`w-full py-3 rounded-lg font-semibold transition-all mt-auto text-lg ${
                                        selectedPlan === 'yearly'
                                            ? 'bg-primary-green text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {selectedPlan === 'yearly' ? 'Selected' : 'Select Plan'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="relative z-10 py-16 px-4">
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white text-center mb-12">
                        Frequently Asked Questions
                    </h2>
                    <div className="space-y-4">
                        {faqs.map((faq, index) => (
                            <div
                                key={index}
                                className="bg-white/90 backdrop-blur-sm rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300"
                            >
                                <button
                                    onClick={() => toggleFaq(index)}
                                    className="w-full px-6 py-5 flex justify-between items-center text-left hover:bg-gray-100 transition-colors duration-200"
                                    style={{ minHeight: '44px' }}
                                >
                                    <span className="text-3xl font-semibold text-primary-blue pr-4">
                                        {faq.question}
                                    </span>
                                    {openFaq === index ? (
                                        <ChevronUp className="w-6 h-6 text-primary-orange flex-shrink-0" />
                                    ) : (
                                        <ChevronDown className="w-6 h-6 text-primary-orange flex-shrink-0" />
                                    )}
                                </button>
                                {openFaq === index && (
                                    <div className="px-6 pb-5 text-2xl text-gray-700 leading-relaxed animate-fade-in">
                                        {faq.answer}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Support */}
            <section className={`relative z-10 py-16 px-4 ${isModal ? 'pb-4' : ''}`}>
                <div className="max-w-4xl mx-auto text-center">
                    <Mail className="w-16 h-16 text-white mx-auto mb-4" />
                    <h2 className="text-5xl sm:text-6xl font-bold text-white mb-4">
                        Any additional questions?
                    </h2>
                    <p className="text-2xl text-white/90 mb-6">
                        Drop us a line at{' '}
                        <a href="mailto:support@mifamli.com" className="text-white font-semibold hover:underline">
                            support@mifamli.com
                        </a>
                    </p>
                </div>
            </section>

            {/* Bottom padding for modal to allow scrolling above sticky button */}
            {isModal && <div className="pb-32"></div>}
            </div>

            {/* Sticky Purchase Button - Different behavior for modal vs page */}
            {isModal ? (
                <div className="sticky bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-white/30 shadow-2xl z-40 py-4 px-4 rounded-b-2xl">
                    <div className="max-w-4xl mx-auto">
                        <button
                            onClick={handlePurchase}
                            className="btn-pulse relative w-full py-4 rounded-xl font-bold text-xl text-white hover:scale-105 transform transition-all duration-200 backdrop-blur-md border-4 border-white/50 overflow-hidden group mb-2"
                            style={{
                                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.85) 0%, rgba(236, 72, 153, 0.85) 100%)',
                                boxShadow: '0 0 35px rgba(59, 130, 246, 0.8), 0 0 70px rgba(236, 72, 153, 0.8), 0 20px 80px rgba(0, 0, 0, 0.4)'
                            }}
                        >
                            <span className="relative z-10 flex items-center justify-center gap-3">
                                Purchase Now - {selectedPlan === 'yearly' ? '₹1,188/year' : '₹2,388/year'}
                                <ArrowRight className="w-5 h-5" />
                            </span>
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/50 to-pink-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                        </button>
                        <p className="text-center text-lg text-gray-700">
                            Cancel anytime • No risk • No penalties • No additional fees • Totally secure
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-white/30 shadow-2xl z-40 py-4 px-4">
                        <div className="max-w-4xl mx-auto">
                            <button
                                onClick={handlePurchase}
                                className="btn-pulse relative w-full py-4 rounded-xl font-bold text-xl text-white hover:scale-105 transform transition-all duration-200 backdrop-blur-md border-4 border-white/50 overflow-hidden group mb-2"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.85) 0%, rgba(236, 72, 153, 0.85) 100%)',
                                    boxShadow: '0 0 35px rgba(59, 130, 246, 0.8), 0 0 70px rgba(236, 72, 153, 0.8), 0 20px 80px rgba(0, 0, 0, 0.4)'
                                }}
                            >
                                <span className="relative z-10 flex items-center justify-center gap-3">
                                    Purchase Now - {selectedPlan === 'yearly' ? '₹1,188/year' : '₹2,388/year'}
                                    <ArrowRight className="w-5 h-5" />
                                </span>
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/50 to-pink-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                            </button>
                            <p className="text-center text-lg text-gray-700">
                                Cancel anytime • No risk • No penalties • No additional fees • Totally secure
                            </p>
                        </div>
                    </div>
                    {/* Footer Padding for Sticky Button */}
                    <div className="h-32"></div>
                </>
            )}
        </div>
    );
};

export default Premium;

